import React from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  FormHelperText,
  Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { get } from 'lodash';
import { getTransactionTypeOptions } from '../../api/codelist/codeListsSlice';
import { GOODS_AND_SERVICE_CN_CODE } from '../../constants/defaults';
import { isOutOfScopeDocumentType } from '../../utils/documentTypeRules';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';

const PaymentMeansMultiSelect = ({
  id,
  name,
  label = 'Transaction Types',
  control,
  errors,
  readOnly = false,
  invoiceTypeCode,
  onChange,
  helperText,
}) => {
  const { validations } = useTranslatedMessages();
  const forbiddenOutOfScopePositions = new Set([2, 3, 4]);

  const getDisabledReason = (option) => {
    if (isOutOfScopeDocumentType(invoiceTypeCode) && forbiddenOutOfScopePositions.has(option.position)) {
      return invoiceTypeCode === GOODS_AND_SERVICE_CN_CODE
        ? (validations?.credit_note?.invalid_transaction_type_for_out_of_scope ??
            'Not applicable for out of scope credit note type (81)')
        : (validations?.invoice?.invalid_transaction_type_for_out_of_scope ??
            'Not applicable for out of scope invoice type (480)');
    }

    return '';
  };

  const transactionTypeOptions = useSelector(getTransactionTypeOptions);

  // Convert 8-digit code string to array of selected transaction type names
  const codeStringToSelected = (codeString) => {
    if (!codeString || typeof codeString !== 'string' || codeString.length !== 8) {
      return [];
    }

    const selectedNames = [];
    for (let i = 0; i < 8; i++) {
      if (codeString[i] === '1') {
        const position = i + 1;
        const option = transactionTypeOptions.find((opt) => opt.position === position);
        if (option) {
          selectedNames.push(option.name);
        }
      }
    }
    return selectedNames;
  };

  // Convert array of selected transaction type names to 8-digit code string
  const selectedToCodeString = (selectedNames) => {
    const codeArray = ['0', '0', '0', '0', '0', '0', '0', '0'];

    selectedNames.forEach((name) => {
      const option = transactionTypeOptions.find((opt) => opt.name === name);
      if (option && option.position >= 1 && option.position <= 8) {
        codeArray[option.position - 1] = '1';
      }
    });

    return codeArray.join('');
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue="00000000"
      render={({ field }) => {
        // Convert code string to selected names for UI
        const codeString = typeof field.value === 'string' ? field.value : '00000000';
        const selectedNames = codeStringToSelected(codeString);

        const errorMessage = get(errors, name, {})?.message;
        const combinedHelperText = [helperText, errorMessage].filter(Boolean).join(' * ');

        const handleDelete = (valueToDelete, event) => {
          if (event) {
            event.stopPropagation();
            event.preventDefault();
          }

          const newSelectedNames = selectedNames.filter((name) => name !== valueToDelete);
          const newCodeString = selectedToCodeString(newSelectedNames);
          field.onChange(newCodeString);
          if (onChange) onChange(newCodeString);
        };

        const handleChange = (e) => {
          const newSelectedNames = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          const eligibleSelectedNames = newSelectedNames.filter((name) => {
            const option = transactionTypeOptions.find((opt) => opt.name === name);
            return option && !getDisabledReason(option);
          });
          const newCodeString = selectedToCodeString(eligibleSelectedNames);
          field.onChange(newCodeString);
          if (onChange) onChange(newCodeString);
        };

        return (
          <FormControl fullWidth error={!!errorMessage} variant="outlined" size="small">
            <InputLabel id={`${id || name}-label`} shrink={selectedNames.length > 0}>
              {label}
            </InputLabel>

            <Select
              labelId={`${id || name}-label`}
              id={id || name}
              multiple
              value={selectedNames}
              onChange={handleChange}
              input={<OutlinedInput label={label} notched={selectedNames.length > 0} />}
              renderValue={(selected) => {
                const safeSelected = Array.isArray(selected) ? selected : [];
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {safeSelected.map((value) => {
                      const option = transactionTypeOptions.find((opt) => opt.name === value);
                      return (
                        <Chip
                          disabled={readOnly}
                          key={value}
                          label={option ? option.name : value}
                          onDelete={(event) => handleDelete(value, event)}
                          deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()} />}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                );
              }}
              readOnly={readOnly}
            >
              {transactionTypeOptions.map((opt) => {
                const disabledReason = getDisabledReason(opt);
                return (
                  <MenuItem
                    key={opt.name}
                    value={opt.name}
                    disabled={Boolean(disabledReason)}
                    sx={{
                      opacity: disabledReason ? 0.5 : 1,
                      '&.Mui-disabled': {
                        pointerEvents: 'auto',
                      },
                    }}
                  >
                    {disabledReason ? (
                      <Tooltip title={disabledReason} placement="right">
                        <span>{opt.name}</span>
                      </Tooltip>
                    ) : (
                      opt.name
                    )}
                  </MenuItem>
                );
              })}
            </Select>
            {combinedHelperText ? <FormHelperText>{combinedHelperText}</FormHelperText> : null}
          </FormControl>
        );
      }}
    />
  );
};

export default PaymentMeansMultiSelect;
